import { toast } from "react-toastify";

/**
 * Downloads a protected or public report/file directly without opening an empty or unauthorized new tab.
 * Automatically attaches Authorization Bearer token and SelectedOwnerId headers,
 * extracts the exact file name from Content-Disposition if present, and initiates browser download.
 *
 * @param {string} fileUrl - Raw report/file URL or relative path from API
 * @param {string} [fallbackFileName="report.pdf"] - Fallback file name if Content-Disposition is absent
 * @returns {Promise<boolean>} True if downloaded successfully, false otherwise
 */
export const downloadReportFile = async (fileUrl, fallbackFileName = "report.pdf") => {
    if (!fileUrl || typeof fileUrl !== "string") {
        return false;
    }

    const trimmed = fileUrl.trim();
    if (!trimmed) {
        return false;
    }

    const baseUrl = process.env.REACT_APP_UMBRACO || "https://admin.eahs.ae";
    const isAbsolute = trimmed.startsWith("http://") || trimmed.startsWith("https://");
    const fullUrl = isAbsolute
        ? trimmed
        : `${baseUrl}${trimmed.startsWith("/") ? "" : "/"}${trimmed}`;

    try {
        const token = localStorage.getItem("accessTokenforEAHS");
        const selectedOwnerId = localStorage.getItem("currentSubAccountId");

        const headers = {
            Accept: "*/*",
        };

        if (token) {
            headers.Authorization = `Bearer ${token}`;
        }

        if (selectedOwnerId) {
            headers.SelectedOwnerId = selectedOwnerId;
        }

        const response = await fetch(fullUrl, {
            method: "GET",
            headers,
        });

        if (!response.ok) {
            throw new Error(`Failed to download file (HTTP ${response.status})`);
        }

        // Extract filename from Content-Disposition header
        const disposition = response.headers.get("content-disposition");
        let fileName = fallbackFileName;

        if (disposition) {
            const utf8Match = disposition.match(/filename\*=UTF-8''([^;]+)/i);
            const regularMatch = disposition.match(/filename="?([^";]+)"?/i);

            if (utf8Match && utf8Match[1]) {
                fileName = decodeURIComponent(utf8Match[1].trim());
            } else if (regularMatch && regularMatch[1]) {
                fileName = regularMatch[1].trim();
            }
        }

        const blob = await response.blob();
        const blobUrl = window.URL.createObjectURL(blob);

        const anchor = document.createElement("a");
        anchor.href = blobUrl;
        anchor.download = fileName;
        anchor.style.display = "none";
        document.body.appendChild(anchor);
        anchor.click();
        document.body.removeChild(anchor);

        setTimeout(() => {
            window.URL.revokeObjectURL(blobUrl);
        }, 1500);

        return true;
    } catch (error) {
        console.error("downloadReportFile error:", error);
        toast.error("Failed to download file");
        return false;
    }
};

export default downloadReportFile;

